import { useEffect, useMemo, useState } from "react";
import { jwtDecode } from 'jwt-decode';
import Modal from "@/common/components/Modal";
import Button from "@/common/components/Button";
import { ClockIcon, EyeIcon, EyeOffIcon } from "lucide-react";
import { Duration, intervalToDuration } from "date-fns";
import { cn } from "@/common/utils/cn";
import TextField from "@/common/components/TextField";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { renewSessionSchema, RenewSessionValidationSchema } from "@/features/auth/schemas/login.schema";
import { useCurrentUser } from "@/common/hooks/useCurrentUser";
import { useAuth } from "@/common/stores/auth";
import { useMutation } from "@tanstack/react-query";
import { login } from "@/features/auth/services/login.service";
import { setInLocalStorage } from "@/common/services/localStorageGetSet";
import { authenticationMethods } from "@/features/users/constants/authenticationMethodOptions.constant";
import { loginWithGoogle } from "@/features/auth/services/loginWithGoogle.service";
import { useLocation } from "react-router-dom";

const RenewSession = () => {
  const [isOpen, setIsOpen] = useState(false);

  const location = useLocation();

  const from = location?.state?.from?.pathname || '/';

  const { updateAccessToken, accessToken } = useAuth();

  const [sessionDuration, setSessionDuration] = useState<Duration>();

  const [showPassword, setShowPassword] = useState(false);

  const { data: currentUser } = useCurrentUser({});
  const isOauthUser = currentUser?.authentication_method === authenticationMethods.oauth;
  const isPasswordUser = currentUser?.authentication_method === authenticationMethods.password;

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors }
  } = useForm<RenewSessionValidationSchema>({
    resolver: zodResolver(renewSessionSchema),
    defaultValues: {
      password: '',
    },
  });

  const {
    mutate: loginMutate,
    isPending: loginIsPending,
  } = useMutation({
    mutationKey: ['login'],
    mutationFn: (form: RenewSessionValidationSchema) => {
      return login({
        email: currentUser?.email!,
        password: form.password,
      });
    },
    onSuccess: (data) => {
      setIsOpen(false);

      setInLocalStorage('access-token', data.accessToken);
      updateAccessToken(data.accessToken);
    },
    onError: (error) => {
      setError('password', { message: error.message });
    }
  });

  const onSubmit: SubmitHandler<RenewSessionValidationSchema> = (data) => loginMutate(data);

  const handleRedirectGoogleLogin = () => {
    loginWithGoogle(from);
  }

  const formattedSessionDuration = useMemo(() => {
    if (!sessionDuration) return '--';

    const { hours, minutes } = sessionDuration;

    if (!hours && !minutes) {
      return '< 1m restante'
    };

    const hPart = hours ? `${hours}h` : '';
    const mPart = minutes ? `${minutes.toString().padStart(2, '0')}m` : '';

    return `${hPart} ${mPart} restantes`.trim();
  }, [sessionDuration?.hours, sessionDuration?.minutes]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const decodedToken = jwtDecode(accessToken);
    const exp = decodedToken.exp;

    if (exp) {
      const expireDate = new Date(Number(exp) * 1000);

      const updateDuration = () => {
        setSessionDuration(intervalToDuration({
          start: Date.now(),
          end: expireDate,
        }));
      }

      updateDuration();

      const timer = setInterval(updateDuration, 60000);

      return () => clearInterval(timer);
    }
  }, [accessToken]);

  useEffect(() => {
    reset();
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      Trigger={(
        <Button
          variant="link-white"
          className={cn(
            'group text-xs italic font-light bg-transparent gap-1 transition-all',
            {
              'text-warning': Number(sessionDuration?.hours || 0) < 2,
              'text-danger-soft': Number(sessionDuration?.hours || 0) < 1,
            },
          )}
        >
          <ClockIcon size={12} strokeWidth={1.5} />

          <span className={cn('group-hover:hidden', isOpen && 'hidden')}>
            {formattedSessionDuration}
          </span>

          <span className={cn('hidden group-hover:inline underline tracking-wide', isOpen && 'inline')}>
            Renovar sessão
          </span>
        </Button>
      )}
      title="Estender tempo de sessão"
      useCancellableFooter={!isOauthUser}
      cancelButtonClassName="bg-white-200 rounded-es-lg"
      confirmButtonClassName="bg-blue-700 rounded-ee-lg"
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonIsLoading={isOauthUser ? false : loginIsPending}
      body={(
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-3 text-white-800"
        >
          <p className="text-xs">
            Para renovar o token de acesso e reiniciar o contador de tempo, confirme suas credenciais.
          </p>

          {isOauthUser && (
            <>
              <Button
                className="w-full bg-white-200 hover:bg-white-300 text-white-800 rounded-b-lg rounded-t-md py-4 transition-colors"
                onClick={handleRedirectGoogleLogin}
              >
                <img src='/Google.svg' />

                Entre com o Google
              </Button>
            </>
          )}

          {isPasswordUser && (
            <TextField
              label="Digite sua senha"
              type={showPassword ? 'text' : 'password'}
              RightIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
              onIconClick={() => setShowPassword(!showPassword)}
              variant={errors.password?.message ? 'error' : 'default'}
              errorMessage={errors.password?.message || undefined}
              {...register('password')}
              markAsRequired
            />
          )}
        </form>
      )}
    />
  );
}

export default RenewSession;
